import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

import { AdFetchingStatus } from '@expPlatform/embeds/ads/constants'
import { getFWAIAds } from '@expPlatform/embeds/ads/utils/adTag'

import type { Api_AdPlacementUnit, Api_Video } from '@core/api'
import { Api_AdPlacementType } from '@core/api'
import uniqBy from '@core/lodash/uniqBy'
import type { IAnyWidgetParams } from '@core/types'
import type { AnyWidgetAppContext } from '@embed/hooks/useEmbedAppContext'

interface IProps {
  appContext: AnyWidgetAppContext | null
  params: IAnyWidgetParams
}

interface IResult {
  loading: boolean
  adVideos: Api_Video[]
  positions: number[]
  actions: {
    loadMore: () => void
  }
}

/**
 *
 */
export const useAdInFeed = (props: IProps): IResult => {
  const { appContext, params } = props

  const adPlacement: Api_AdPlacementUnit | undefined = useMemo(() => {
    return appContext?.adConfig?.placements?.find(
      (placement) =>
        placement.placement_type === Api_AdPlacementType.RM_INFEED ||
        placement.placement_type === Api_AdPlacementType.OUTSTREAM,
    )
  }, [appContext])

  const [state, setState] = useState<{
    loading: boolean
    adVideos: Api_Video[]
    positions: number[]
  }>({
    loading: false,
    adVideos: [],
    positions: [],
  })

  // useLayoutEffect so consumers (e.g. widget render-outcome) see loading=true in the
  // same commit as placement context, before useEffects that might lock a terminal status.
  useLayoutEffect(() => {
    if (!params.vast_attrs && params.wait_for_vast_attrs === true) {
      return
    }
    if (!!adPlacement && adPlacement.ad_tags.length > 0) {
      setState((state) => ({
        ...state,
        loading: true,
      }))
    }
  }, [adPlacement, params.vast_attrs, params.wait_for_vast_attrs])

  const loadAds = useCallback(
    async (
      adUnit: Api_AdPlacementUnit,
    ): Promise<ReturnType<typeof getFWAIAds>> => {
      const response = await getFWAIAds(adUnit.ad_tags[0], params)
      if (response.status === AdFetchingStatus.OK) {
        setState((state) => {
          let adVideos = uniqBy(
            state.adVideos.concat(response.ads),
            (video) => video.encoded_id,
          )

          if (params.allow_duplicate_ads) {
            const counts: Record<string, number> = {}
            adVideos = response.ads.map((ad) => {
              counts[ad.encoded_id] ??= -1
              const count = ++counts[ad.encoded_id]
              let suffix = ''
              if (count > 0) {
                suffix = `--${Math.random()}`
              }
              return { ...ad, encoded_id: `${ad.encoded_id}${suffix}` }
            })
          }
          return {
            loading: false,
            adVideos,
            positions: adUnit.positions || [0],
          }
        })
      } else {
        setState((state) => ({
          ...state,
          loading: false,
        }))
      }
      return response
    },
    // TODO(ZE-231): Fix the deps and re-enable the rule
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adPlacement, params],
  )

  useEffect(() => {
    if (!state.loading) {
      return
    }
    if (!adPlacement) {
      setState((state) => ({
        ...state,
        loading: false,
      }))
      return
    }
    ;(async () => {
      const response = await loadAds(adPlacement)
      // if we are in outstream player and get only one ad from first call, we prefetch next ad with small delay
      if (
        adPlacement.placement_type === Api_AdPlacementType.OUTSTREAM &&
        adPlacement.ad_tags[0]?.ad_sdk !== 'criteo' &&
        response.status === AdFetchingStatus.OK &&
        response.ads.length === 1
      ) {
        setTimeout(async () => {
          await loadAds(adPlacement)
        }, 3000)
      }
    })()
    // TODO(ZE-231): Fix the deps and re-enable the rule
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adPlacement, state.loading, params.vast_attrs])

  const loadMore = useCallback(() => {
    if (
      adPlacement &&
      adPlacement.placement_type === Api_AdPlacementType.OUTSTREAM &&
      adPlacement.ad_tags[0]?.ad_sdk !== 'criteo'
    ) {
      loadAds(adPlacement)
    }
  }, [adPlacement, loadAds])

  return useMemo(
    () => ({
      ...state,
      actions: {
        loadMore,
      },
    }),
    [state, loadMore],
  )
}
